import {
	AfterViewChecked,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import {
	catchError,
	combineLatest,
	filter,
	forkJoin,
	from,
	map,
	Observable,
	of,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs';
import { SubSink } from 'subsink';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

// --- Animations ---
import { fader } from '@animations/route-animations';

// --- Facades ---
import { AppearanceFacade } from '@facades/appearance.facade';
import { AuthFacade } from '@facades/auth.facade';
import { MenuLayoutFacade } from '@facades/menu-layout.facade';
import { ParametersFacade } from '@facades/parameters.facade';
import { StoresFacade } from '@facades/stores.facade';
import { TenantsFacade } from '@facades/tenants.facade';

// --- Services ---
import { LocalStorageService } from '@services/local-storage.service';

// --- Interfaces ---
import type { IParameters } from '@interfaces/parameters';
import { EUserProfileIds } from '@interfaces/user';
import { VersionDialogService } from '../services/version-dialog.service';

@Component({
	selector: 'app-pages',
	templateUrl: './pages.component.html',
	styleUrls: ['./pages.component.scss'],
	animations: [fader],
	providers: [DialogService],
})
export class PagesComponent implements OnInit, OnDestroy, AfterViewChecked {
	private subs = new SubSink();

	ref?: DynamicDialogRef;
	hasCategoryMenu = true;

	constructor(
		private _cdRef: ChangeDetectorRef,
		private _router: Router,
		public _dialogService: DialogService,
		private appearanceFacade: AppearanceFacade,
		private authFacade: AuthFacade,
		private localStorageService: LocalStorageService,
		private menuLayoutFacade: MenuLayoutFacade,
		private parametersFacade: ParametersFacade,
		private storeFacade: StoresFacade,
		private tenantsFacade: TenantsFacade,
		private versionDialogService: VersionDialogService
	) {}

	ngOnInit() {
		this.loadUserEnvironment();
		this.handleRouteOnChange();
	}

	ngAfterViewChecked() {
		this._cdRef.detectChanges();
	}

	ngOnDestroy() {
		this.subs.unsubscribe();
	}

	prepareRoute(outlet: RouterOutlet) {
		const animation = outlet?.activatedRouteData?.['animation'] as string;
		return animation ?? null;
	}

	get loadedTheme() {
		return this.appearanceFacade.loadedTheme;
	}

	get showMenu() {
		return this.appearanceFacade.showMenu;
	}

	private handleRouteOnChange() {
		this.subs.add(
			this._router.events
				.pipe(
					filter((event: any) => event instanceof NavigationStart),
					switchMap(event =>
						combineLatest([from(event.url), this.authFacade.authState$])
					)
				)
				.subscribe(([currentURL, authState]) => {
					const hasUser = Boolean(authState.user);
					if (!hasUser) return;

					const isPreferencesPage = currentURL === '/preferences';
					if (isPreferencesPage) {
						const isPortalManager =
							authState.user?.profileId === EUserProfileIds.PORTAL_MANAGER;
						if (isPortalManager) {
							this._router.navigate(['/preferences/records/tenants']);
						}
					}
				})
		);
	}

	private loadUserEnvironment() {
		this.subs.add(
			this.authFacade.authState$
				.pipe(
					map(({ user }) => user),
					switchMap(user => {
						if (!user) {
							return this.setParameters().pipe(
								switchMap(() => of(null)),
								catchError(() => {
									this.ensureThemeLoaded();
									return of(null);
								}),
								shareReplay()
							);
						}

						if (!user.tenantId) {
							this.ensureThemeLoaded();
							return of(user);
						}

						const sources: Observable<any>[] = [
							this.parametersFacade.getParameters().pipe(
								switchMap(parameters => this.setParameters(parameters)),
								shareReplay()
							),
							this.tenantsFacade.getTenantById(user.tenantId).pipe(
								tap(tenant => this.tenantsFacade.syncLocalTenant(tenant)),
								shareReplay()
							),
						];

						const isMenuLayoutEmpty = !this.menuLayoutFacade.menuLayout;
						if (isMenuLayoutEmpty) {
							sources.push(
								this.menuLayoutFacade.getMenuLayout().pipe(shareReplay())
							);
						}

						return forkJoin(sources).pipe(
							switchMap(() => of(user)),
							catchError(() => {
								// 401/refresh failure must not leave *ngIf="loadedTheme" blank forever.
								this.ensureThemeLoaded();
								return of(user);
							}),
							shareReplay()
						);
					})
				)
				.subscribe(user => {
					if (!user) return;

					const isPortalManager =
						user?.profileId === EUserProfileIds.PORTAL_MANAGER;
					if (isPortalManager) {
						this.hasCategoryMenu = false;
						this.ensureThemeLoaded();
					}
				})
		);
	}

	private setParameters(parameters?: IParameters) {
		if (!parameters) parameters = this.parametersFacade.parameters;

		const defaultStoreId =
			parameters?.defaultStorehouseId ?? parameters?.defaultStoreId;

		return forkJoin([
			this.handleStore(defaultStoreId),
			this.handleTheme(parameters?.themeId),
		]);
	}

	private handleStore(id: IParameters['defaultStoreId']) {
		// of(null) — never EMPTY inside forkJoin (EMPTY never emits → blank shell).
		if (!id) return of(null);
		if (this.storeFacade.currentStore) return of(null);

		return this.storeFacade.getStoreById(id).pipe(
			tap(store => {
				this.storeFacade.currentStore = store;
				this.localStorageService.set('store', store);
			}),
			catchError(() => of(null))
		);
	}

	private handleTheme(id: IParameters['themeId']) {
		if (!id) {
			this.ensureThemeLoaded();
			return of(null);
		}
		if (this.appearanceFacade.loadedTheme) return of(null);

		return this.appearanceFacade.getThemeById(id).pipe(
			tap({
				next: theme => {
					this.appearanceFacade.setStorageTheme(theme);
					this.ensureThemeLoaded();
					this.versionDialogService.checkAndShowDialog();
				},
				error: () => {
					this.ensureThemeLoaded();
				},
			}),
			catchError(() => {
				this.ensureThemeLoaded();
				return of(null);
			})
		);
	}

	private ensureThemeLoaded() {
		// If bootstrap failed mid-way, still re-apply a cached theme so the shell
		// does not look like an unthemed visitor while the user is logged in.
		this.appearanceFacade.getStorageTheme();
		this.appearanceFacade.loadedTheme = true;
	}
}
